// src/modules/config/components/CategoryModal.tsx - ПОЛНАЯ ВЕРСИЯ с многоязычностью и иерархией
import React, { useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Row,
  Col,
  Tabs
} from 'antd';
import { MenuCategory } from '@/types';

interface CategoryModalProps {
  visible: boolean;
  category: MenuCategory | null;
  categories: MenuCategory[]; // Для выбора родительской категории
  loading: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
}

export const CategoryModal: React.FC<CategoryModalProps> = ({
  visible,
  category,
  categories,
  loading,
  onCancel,
  onSubmit
}) => {
  const [form] = Form.useForm();
  const isEdit = !!category;

  // Функция для генерации slug из названия
  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[а-я]/g, (char) => {
        const map: { [key: string]: string } = {
          'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo',
          'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
          'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
          'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
          'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya'
        };
        return map[char] || char;
      })
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  useEffect(() => {
    if (visible) {
      if (category) {
        // Редактирование - заполняем форму
        form.setFieldsValue({
          name: category.name,
          name_kz: category.name_kz || '',
          slug: category.slug,
          icon: category.icon || '',
          parent: category.parent,
          sort_order: category.sort_order,
          is_active: category.is_active
        });
      } else {
        // Создание - очищаем форму с дефолтными значениями
        form.resetFields();
        form.setFieldsValue({
          sort_order: 0,
          is_active: true,
          parent: null
        });
      }
    }
  }, [visible, category, form]);

  // Обработчик изменения названия для автогенерации slug
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    if (name && !isEdit) {
      const slug = generateSlug(name);
      form.setFieldsValue({ slug });
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      // Если slug не заполнен, генерируем его из названия
      if (!values.slug && values.name) {
        values.slug = generateSlug(values.name);
      }
      
      await onSubmit(values);
      form.resetFields();
    } catch (error) {
      console.error('Ошибка при отправке формы категории:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Фильтруем категории для родительской (исключаем саму категорию при редактировании)
  const parentCategoryOptions = categories.filter(cat => 
    !isEdit || cat.id !== category?.id
  );

  // Формируем элементы для табов
  const tabItems = [
    {
      key: 'main',
      label: 'Основная информация',
      children: (
        <div>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Название (русский)"
                rules={[{ required: true, message: 'Введите название категории' }]}
              >
                <Input 
                  placeholder="Название категории на русском" 
                  onChange={handleNameChange}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name_kz"
                label="Название (казахский)"
              >
                <Input placeholder="Название категории на казахском" />
              </Form.Item>
            </Col>
            <Col span={8}>  // Изменить span с 12 на 8
              <Form.Item 
                name="name_en" 
                label="Название (английский)"
              >
                <Input placeholder="Название категории на английском" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="slug"
                label="Slug (URL)"
                rules={[{ required: true, message: 'Введите slug' }]}
              >
                <Input placeholder="category-slug" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="icon"
                label="Иконка"
              >
                <Input placeholder="shopping-cart, utensils, coffee" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="parent"
                label="Родительская категория"
              >
                <Select
                  placeholder="Выберите родительскую категорию"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {parentCategoryOptions.map(cat => (
                    <Select.Option key={cat.id} value={cat.id}>
                      {cat.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="sort_order"
                label="Порядок сортировки"
                initialValue={0}
              >
                <InputNumber 
                  min={0} 
                  style={{ width: '100%' }}
                  placeholder="0" 
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="is_active"
                label="Статус"
                initialValue={true}
              >
                <Select>
                  <Select.Option value={true}>Активна</Select.Option>
                  <Select.Option value={false}>Неактивна</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </div>
      )
    }
  ];

  return (
    <Modal
      title={isEdit ? "Редактировать категорию" : "Создать категорию"}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Tabs items={tabItems} />
        
        <Form.Item style={{ marginTop: 16, marginBottom: 0 }}>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading} size="large">
              {isEdit ? 'Обновить категорию' : 'Создать категорию'}
            </Button>
            <Button onClick={handleCancel} size="large">
              Отмена
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};